'use client';

import { useState, useEffect, FormEvent } from 'react';
import { useRouter } from 'next/navigation';
import api, { KUNCI_ALASAN_KELUAR } from '@/lib/axios';
import useAuthStore from '@/stores/authStore';
import Swal from 'sweetalert2';
import { pesanLogIn } from '@/components/OtorisasiModal';

/** Galat axios yang dibaca halaman login (pesan BE / jaringan). */
type GalatLogin = { response?: { data?: { message?: unknown; error?: unknown } }; message?: unknown };

export default function LoginPage() {
    const router = useRouter();
    const setLogin = useAuthStore((state) => state.setLogin);
    const accessToken = useAuthStore((state) => state.accessToken);

    useEffect(() => {
        if (accessToken) {
            router.replace('/welcome');
        }
    }, [accessToken, router]);

    // Sesi diakhiri paksa (401 di halaman lain, lib/axios.ts) — mis. user dinonaktifkan administrator: tampilkan sekali
    useEffect(() => {
        let alasan: string | null = null;
        try {
            alasan = window.sessionStorage.getItem(KUNCI_ALASAN_KELUAR);
            if (alasan) window.sessionStorage.removeItem(KUNCI_ALASAN_KELUAR);
        } catch { /* storage diblokir */ }
        if (alasan) void Swal.fire({ icon: 'warning', title: 'Sesi Berakhir', text: alasan });
    }, []);

    const [formData, setFormData] = useState({
        kdmuser: '',
        pwd: ''
    });
    const [loading, setLoading] = useState(false);
    const [showPwd, setShowPwd] = useState(false);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        // Delphi TFormMGSYLogIn.ButtonOkClick (MGSYLoginForm.pas:166-200): panjang user name lalu password < 3 → pesan
        // wmLoginFieldLength (selalu gagal apa pun isi DB, jadi dicek tanpa memanggil BE; BE memeriksa ulang)
        if (formData.kdmuser.length < 3 || formData.pwd.length < 3) {
            await Swal.fire({
                icon: 'warning',
                title: 'Peringatan',
                text: pesanLogIn.panjangMinimal(formData.kdmuser.length < 3 ? 'user name' : 'password'),
            });
            return;
        }
        setLoading(true);

        try {
            const response = await api.post('/auth/login', formData);

            if (response.data.success) {
                setLogin(response.data.data);
                Swal.fire({
                    icon: 'success',
                    title: 'Login Berhasil',
                    text: 'Selamat datang kembali!',
                    timer: 1500,
                    showConfirmButton: false
                });
                router.push('/welcome');
            }
        } catch (error: unknown) {
            console.error('[LoginError]', error);
            const g = error as GalatLogin;
            const dataGalat = g.response?.data;
            const serverMessage = typeof dataGalat?.message === 'string' && dataGalat.message
                ? dataGalat.message
                : (typeof dataGalat?.error === 'string' ? dataGalat.error : '');
            if (serverMessage) {
                // Pesan Log In Delphi dari BE ('User name atau password salah.', 'User tidak aktif.', ...) =
                // FGlobal.ShowWarningMessage
                Swal.fire({ icon: 'warning', title: 'Peringatan', text: serverMessage });
            } else {
                const pesanJaringan = typeof g.message === 'string' ? g.message : '';
                Swal.fire({
                    icon: 'error',
                    title: 'Login Gagal',
                    text: pesanJaringan === 'Network Error'
                        ? 'Tidak dapat terhubung ke server backend (Network Error). Pastikan Backend berjalan di port 3045.'
                        : (pesanJaringan || 'Terjadi kesalahan pada server'),
                });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative flex min-h-screen items-center justify-center overflow-hidden p-4">
            {/* Background gradient sky */}
            <div className="absolute inset-0 bg-gradient-to-b from-sky-300 via-sky-100 to-white" />

            {/* Soft cloud layers */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute -top-24 -left-32 h-96 w-96 rounded-full bg-white/50 blur-3xl" />
                <div className="absolute top-1/3 -right-32 h-[28rem] w-[28rem] rounded-full bg-white/40 blur-3xl" />
                <div className="absolute -bottom-32 left-1/4 h-[32rem] w-[32rem] rounded-full bg-white/60 blur-3xl" />
                <div className="absolute bottom-10 right-1/4 h-72 w-72 rounded-full bg-sky-200/50 blur-3xl" />
            </div>

            {/* Decorative subtle arcs */}
            <svg
                className="pointer-events-none absolute inset-0 h-full w-full opacity-40"
                viewBox="0 0 1200 800"
                fill="none"
                preserveAspectRatio="xMidYMid slice"
            >
                <path d="M -100 500 Q 600 200 1300 500" stroke="white" strokeWidth="1" fill="none" />
                <path d="M -100 600 Q 600 350 1300 600" stroke="white" strokeWidth="1" fill="none" />
            </svg>

            {/* Glass card */}
            <div className="relative w-full max-w-md rounded-3xl border border-white/60 bg-white/40 p-8 shadow-[0_8px_40px_rgba(56,132,255,0.15)] backdrop-blur-xl">
                {/* Icon */}
                <div className="mb-5 flex justify-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/70 bg-white/80 shadow-sm backdrop-blur">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-slate-700"
                        >
                            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                            <polyline points="10 17 15 12 10 7" />
                            <line x1="15" y1="12" x2="3" y2="12" />
                        </svg>
                    </div>
                </div>

                <h1 className="text-center text-2xl font-semibold text-slate-800">
                    Masuk ke MGMX
                </h1>
                <p className="mx-auto mt-2 max-w-xs text-center text-sm text-slate-600">
                    Sistem manajemen trucking untuk operasional keuangan, gudang, dan penjualan Anda.
                </p>

                <form onSubmit={handleSubmit} className="mt-6 space-y-3">
                    {/* Username */}
                    <div className="group flex items-center gap-2 rounded-xl border border-white/70 bg-white/70 px-3.5 py-3.5 shadow-sm transition focus-within:border-sky-300 focus-within:bg-white focus-within:ring-2 focus-within:ring-sky-200/70">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-slate-400"
                        >
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                        </svg>
                        <input
                            type="text"
                            autoComplete="username"
                            className="w-full bg-transparent text-sm text-slate-800 placeholder-slate-400 outline-none"
                            value={formData.kdmuser}
                            onChange={(e) => setFormData({ ...formData, kdmuser: e.target.value })}
                            placeholder="Username"
                        />
                    </div>

                    {/* Password */}
                    <div className="group flex items-center gap-2 rounded-xl border border-white/70 bg-white/70 px-3.5 py-3.5 shadow-sm transition focus-within:border-sky-300 focus-within:bg-white focus-within:ring-2 focus-within:ring-sky-200/70">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-slate-400"
                        >
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                        <input
                            type={showPwd ? 'text' : 'password'}
                            autoComplete="current-password"
                            className="w-full bg-transparent text-sm text-slate-800 placeholder-slate-400 outline-none"
                            value={formData.pwd}
                            onChange={(e) => setFormData({ ...formData, pwd: e.target.value })}
                            placeholder="Password"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPwd((s) => !s)}
                            className="text-slate-400 transition hover:text-slate-600"
                            aria-label={showPwd ? 'Sembunyikan password' : 'Tampilkan password'}
                            tabIndex={-1}
                        >
                            {showPwd ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                    <line x1="1" y1="1" x2="23" y2="23" />
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                </svg>
                            )}
                        </button>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="mt-4 w-full rounded-xl bg-slate-900 py-3.5 text-sm font-medium text-white shadow-md transition hover:bg-slate-800 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {loading ? 'Memproses…' : 'Masuk'}
                    </button>
                </form>
            </div>
        </div>
    );
}
